import { MessageSquarePlus, Plus, Edit2, Trash2, BarChart3 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import Button from '../../components/Button';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import EmptyState from '../../components/EmptyState';
import ErrorDisplay from '../../components/ErrorDisplay';
import LoadingSpinner from '../../components/LoadingSpinner';
import { Toggle } from '../../components/Toggle';
import type { FeedbackCampaign } from '../../types/dynamic-feedback';
import { CampaignEditorModal } from './components/CampaignEditorModal';
import { CampaignResponsesModal } from './components/CampaignResponsesModal';
import { feedbackCampaignsService } from './services/feedbackCampaignsService';

export const FeedbackCampaigns = () => {
  const [campaigns, setCampaigns] = useState<FeedbackCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [responseCounts, setResponseCounts] = useState<Record<string, number>>({});

  // Editor modal state
  const [editorModal, setEditorModal] = useState<{
    show: boolean;
    campaign: FeedbackCampaign | null;
  }>({ show: false, campaign: null });

  // Responses modal state
  const [responsesModal, setResponsesModal] = useState<{
    show: boolean;
    campaign: FeedbackCampaign | null;
  }>({ show: false, campaign: null });

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean;
    campaign: FeedbackCampaign | null;
  }>({ show: false, campaign: null });

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedCampaigns = await feedbackCampaignsService.getAllCampaigns();
      setCampaigns(fetchedCampaigns);

      // Load response counts
      const counts: Record<string, number> = {};
      await Promise.all(
        fetchedCampaigns.map(async (campaign) => {
          const count = await feedbackCampaignsService.getResponseCount(campaign.id);
          counts[campaign.id] = count;
        })
      );
      setResponseCounts(counts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const handleCreateNew = () => {
    setEditorModal({ show: true, campaign: null });
  };

  const handleEdit = (campaign: FeedbackCampaign) => {
    setEditorModal({ show: true, campaign });
  };

  const handleViewResponses = (campaign: FeedbackCampaign) => {
    setResponsesModal({ show: true, campaign });
  };

  const handleDelete = (campaign: FeedbackCampaign) => {
    setDeleteConfirm({ show: true, campaign });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.campaign) return;

    try {
      await feedbackCampaignsService.deleteCampaign(deleteConfirm.campaign.id);
      await loadCampaigns();
      setDeleteConfirm({ show: false, campaign: null });
    } catch (err) {
      console.error('Error deleting campaign:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete campaign');
    }
  };

  const handleToggleStatus = async (campaign: FeedbackCampaign) => {
    try {
      const newStatus = campaign.status === 'active' ? 'draft' : 'active';
      await feedbackCampaignsService.updateCampaign(campaign.id, { status: newStatus });
      await loadCampaigns();
    } catch (err) {
      console.error('Error toggling status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update campaign status');
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6">
        <ErrorDisplay message={error} />
        <div className="mt-4 text-center">
          <Button onClick={loadCampaigns} variant="secondary">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <MessageSquarePlus className="w-6 h-6" />
              Feedback Campaigns
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Create and manage dynamic feedback surveys for different app locations
            </p>
          </div>
          <Button onClick={handleCreateNew} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New Campaign
          </Button>
        </div>
      </div>

      {/* Campaigns List */}
      {campaigns.length === 0 ? (
        <EmptyState
          icon={<MessageSquarePlus className="mx-auto h-12 w-12 text-gray-400" />}
          title="No feedback campaigns yet"
          description="Create your first campaign to start collecting feedback from users"
          action={{
            label: 'Create Campaign',
            onClick: handleCreateNew,
            variant: 'primary',
          }}
        />
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Campaign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Locations
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Questions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Responses
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {campaigns.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {campaign.description}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(
                          campaign.status
                        )}`}
                      >
                        {campaign.status}
                      </span>
                      <Toggle
                        checked={campaign.status === 'active'}
                        onChange={() => handleToggleStatus(campaign)}
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {campaign.targetLocations.slice(0, 2).map((location) => (
                        <span
                          key={location}
                          className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                        >
                          {location}
                        </span>
                      ))}
                      {campaign.targetLocations.length > 2 && (
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                          +{campaign.targetLocations.length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {campaign.questions.length}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleViewResponses(campaign)}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <BarChart3 className="w-4 h-4" />
                      {responseCounts[campaign.id] || 0}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(campaign)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(campaign)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {editorModal.show && (
        <CampaignEditorModal
          campaign={editorModal.campaign}
          onClose={() => setEditorModal({ show: false, campaign: null })}
          onSaved={loadCampaigns}
        />
      )}

      {responsesModal.show && responsesModal.campaign && (
        <CampaignResponsesModal
          campaign={responsesModal.campaign}
          onClose={() => setResponsesModal({ show: false, campaign: null })}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm.show && deleteConfirm.campaign && (
        <ConfirmationDialog
          isOpen={deleteConfirm.show}
          title="Delete Campaign"
          message={`Are you sure you want to delete "${deleteConfirm.campaign.name}"? This will also delete all associated questions and cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirm({ show: false, campaign: null })}
          confirmVariant="danger"
        />
      )}
    </div>
  );
};
